import { EventContentArg } from '@fullcalendar/core';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import ContactsIcon from '@mui/icons-material/Contacts';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GoogleIcon from '@mui/icons-material/Google';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import RoomIcon from '@mui/icons-material/Room';
import TodayIcon from '@mui/icons-material/Today';
import { Button, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { useModal } from 'mui-modal-provider';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { contactRoutes } from '../../../routes';
import { CalendarEvent } from '../../../sic-lib/types/CalendarEvent';
import { formatStartEndDates } from '../../../sic-lib/utils/calendarEventHelpers';
import { useAppDispatch } from '../../../store/hooks';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';
import CalendarEventOutcomePopover from '../CalendarEventOutcomePopover/CalendarEventOutcomePopover';

const useStyles = makeStyles()(theme => ({
    root: {
        width: '448px',
        padding: theme.spacing(2),
        flexGrow: 1,
    },
    lineItem: {
        width: '100%',
        display: 'flex',
        minHeight: '40px',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    lineBody: {
        flex: '1 1 auto',
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    inlineIconWrap: {
        flex: '0 0 72px',
        padding: theme.spacing(2),
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignSelf: 'flex-start',
    },
    typography: {
        //padding: theme.spacing(2),
    },
    inlineIcon: {
        color: theme.palette.text.secondary,
    },
}));

interface Props {
    fcCalendarEvent: EventContentArg;
    calendarEvent: CalendarEvent;
    closePopover: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
}

export default function FullCalendarEventPopoverContent({
    fcCalendarEvent,
    calendarEvent,
    closePopover,
}: Props) {
    const { classes } = useStyles();
    const { showModal } = useModal();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const [btnOutcomePopoverEl, setBtnOutcomePopoverEl] = React.useState<HTMLButtonElement | null>(
        null,
    );

    const handleBtnDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Please Confirm',
            description: 'Permanently delete this calendar event from the system?',
            onConfirm: () => {
                //dispatch(calendarActions.deleteCalendarEvent(calendarEvent.eventId));
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };

    const handleBtnEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
        //dispatch(calendarActions.openDrawerEditCalendarEvent(calendarEvent));
        closePopover(null);
    };

    const handleBtnClose = (event: React.MouseEvent<HTMLButtonElement>) => {
        closePopover(null);
    };

    const handleNavigate = () => {
        if (calendarEvent.contactId) {
            history.push(contactRoutes.setDetails(calendarEvent.contactId));
        }
    };

    return (
        <React.Fragment>
            <Grid container>
                <Grid container direction="column" alignItems="flex-start" className={classes.root}>
                    <Grid container direction="column" spacing={1}>
                        <Grid item container direction="row" justifyContent="flex-end">
                            <Grid item>
                                <IconButton onClick={handleBtnDelete} size="large">
                                    <DeleteIcon />
                                </IconButton>
                                <IconButton onClick={handleBtnEdit} size="large">
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={handleBtnClose} size="large">
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <div className={classes.lineItem}>
                            <div className={classes.inlineIconWrap}>
                                <Tooltip title="Event Title">
                                    <TodayIcon className={classes.inlineIcon} />
                                </Tooltip>
                            </div>
                            <Typography className={classes.lineBody} variant="h4" component="div">
                                {fcCalendarEvent.event.title}
                            </Typography>
                        </div>
                        <div className={classes.lineItem}>
                            <div className={classes.inlineIconWrap}>
                                <Tooltip title="Contact Name">
                                    <ContactsIcon className={classes.inlineIcon} />
                                </Tooltip>
                            </div>
                            <Typography className={classes.lineBody} component="div">
                                <Tooltip title="Navigate to Contact">
                                    <Button color="primary" onClick={handleNavigate}>
                                        {calendarEvent.contactName || 'Contact Name'}
                                    </Button>
                                </Tooltip>
                            </Typography>
                        </div>
                        <div className={classes.lineItem}>
                            <div className={classes.inlineIconWrap}>
                                <Tooltip title="Event Time">
                                    <AccessTimeIcon className={classes.inlineIcon} />
                                </Tooltip>
                            </div>
                            <Typography className={classes.lineBody} component="div">
                                {formatStartEndDates(calendarEvent)}
                            </Typography>
                        </div>
                        <div className={classes.lineItem}>
                            <div className={classes.inlineIconWrap}>
                                <Tooltip title="Event Owner">
                                    <PersonIcon className={classes.inlineIcon} />
                                </Tooltip>
                            </div>
                            <Typography className={classes.lineBody} component="div">
                                {calendarEvent.owner}
                            </Typography>
                        </div>
                        {calendarEvent.guestEmails && calendarEvent.guestEmails?.length > 0 && (
                            <>
                                <div className={classes.lineItem}>
                                    <div className={classes.inlineIconWrap}>
                                        <Tooltip title="Attendees &amp; Guests">
                                            <GroupIcon className={classes.inlineIcon} />
                                        </Tooltip>
                                    </div>
                                    <Typography className={classes.lineBody} component="div">
                                        {calendarEvent.guestEmails.length} event attendee
                                        {calendarEvent.guestEmails.length > 1 ? 's' : ''}
                                    </Typography>
                                </div>
                                <div className={classes.lineItem}>
                                    <div className={classes.inlineIconWrap}></div>
                                    <Typography className={classes.lineBody} component="div">
                                        {calendarEvent.guestEmails.map((guest, index) => (
                                            <div key={index}>{guest}</div>
                                        ))}
                                    </Typography>
                                </div>
                            </>
                        )}
                        {calendarEvent.location && (
                            <div className={classes.lineItem}>
                                <div className={classes.inlineIconWrap}>
                                    <Tooltip title="Location">
                                        <RoomIcon className={classes.inlineIcon} />
                                    </Tooltip>
                                </div>
                                <Typography className={classes.lineBody} component="div">
                                    {calendarEvent.location}
                                </Typography>
                            </div>
                        )}
                        {calendarEvent.description && (
                            <div className={classes.lineItem}>
                                <div className={classes.inlineIconWrap}>
                                    <Tooltip title="Description">
                                        <EventNoteIcon className={classes.inlineIcon} />
                                    </Tooltip>
                                </div>
                                <Typography className={classes.lineBody} component="div">
                                    {calendarEvent.description}
                                </Typography>
                            </div>
                        )}
                        {calendarEvent?.google?.googleUrl && (
                            <div className={classes.lineItem}>
                                <div className={classes.inlineIconWrap}>
                                    <Tooltip title="Google Info">
                                        <GoogleIcon className={classes.inlineIcon} />
                                    </Tooltip>
                                </div>
                                <Typography className={classes.lineBody} component="div">
                                    <Link href={calendarEvent.google?.googleUrl} target="_blank">
                                        View on Google Calendar
                                    </Link>
                                    {calendarEvent?.google?.meetId && (
                                        <>
                                            <Button
                                                variant="contained"
                                                href={`//meet.google.com/${calendarEvent.google?.meetId}`}
                                                target="_blank"
                                            >
                                                Join with Google Meet
                                            </Button>
                                            <div>
                                                meet.google.com/
                                                {calendarEvent.google?.meetId}
                                            </div>
                                        </>
                                    )}
                                </Typography>
                            </div>
                        )}
                        <div className={classes.lineItem}>
                            <div className={classes.inlineIconWrap}>
                                <EventAvailableIcon className={classes.inlineIcon} />
                            </div>
                            <Typography className={classes.lineBody} component="div">
                                <Tooltip title="Set Event Outcome">
                                    <Button
                                        color={calendarEvent.outcome ? 'primary' : 'secondary'}
                                        onClick={event =>
                                            setBtnOutcomePopoverEl(event.currentTarget)
                                        }
                                    >
                                        {calendarEvent.outcome || 'No Outcome Set'}
                                    </Button>
                                </Tooltip>
                                <CalendarEventOutcomePopover
                                    parentEl={btnOutcomePopoverEl}
                                    calendarEvent={calendarEvent}
                                    setParentEl={setBtnOutcomePopoverEl}
                                />
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
